import React from "react";
import { ActionIcon, Box, Button, Text } from "@mantine/core";
import CalendarDrawer from "../drawer/CalendarDrawer";
import { useSalon } from "../../../../context/SalonContext";
import NestedScreen from "../drawer/NestedScreen";
import { DESIGN_ICONS } from "../../../../theme/designIcons";
import type { SalonService } from "../../../../types/salon";
import { notifySuccess } from "../../../../utils/notify";
import WaitlistList from "./WaitlistList";
import WalkInForm from "./WalkInForm";
import AssignPicker from "./AssignPicker";
import type { WaitlistStore } from "./useWaitlist";
import { useWaitlistActions } from "./useWaitlistActions";
import type { WaitlistDrawerState } from "./useWaitlistDrawer";
import { useStaffAvailability } from "./useStaffAvailability";
import { qualifiedFor } from "./staffAvailability";
import WalkInLink from "./WalkInLink";
import { entryClientName, type WaitlistToast } from "./waitlistModel";
import styles from "./Waitlist.module.css";

/** Desktop toasts: title + note (prototype's two-line toast). */
export const showWaitlistToast = (toast: WaitlistToast) =>
  notifySuccess(toast.note ?? "", { title: toast.title });

export interface WaitlistDrawerProps {
  drawer: WaitlistDrawerState;
  store: WaitlistStore;
  timezone: string;
  services: SalonService[];
  /** A walk-in was booked: put its appointment on the grid. */
  onAssigned: (transactionUuid: string) => void;
}

/**
 * The walk-ins drawer (design handoff §8), drawer slot kind "walkins":
 * "Walk-ins" + count + copy walk-in link, the cards in line
 * order (drag / keyboard reorder, ⋯ Edit / Remove, Assign staff), the empty
 * state, and the sticky New Walk-In footer. New / Edit walk-in is a
 * NestedScreen over the list (slot nested "walkInForm").
 */
export default function WaitlistDrawer(props: WaitlistDrawerProps) {
  const { drawer } = props;
  return (
    <CalendarDrawer
      opened={drawer.opened}
      onClose={drawer.close}
      withHeader={false}
      ariaLabel="Walk-ins"
    >
      {drawer.opened && <WaitlistDrawerBody {...props} />}
    </CalendarDrawer>
  );
}

function WaitlistDrawerBody({
  drawer,
  store,
  timezone,
  services,
  onAssigned,
}: WaitlistDrawerProps) {
  const availability = useStaffAvailability(timezone);
  const { refreshServices } = useSalon() as {
    refreshServices?: () => Promise<unknown>;
  };
  const { rows, nowMs, nowWall } = availability;
  const actions = useWaitlistActions(store, showWaitlistToast, {
    timezone,
    services,
    onAssigned,
    refreshServices,
  });
  const { entries } = store;
  const { form } = drawer;
  const serviceOf = (id: string) => services.find((s) => s.id === id);

  return (
    <>
      <Box className={styles.root}>
        <Box className={styles.head}>
          <Box className={styles.headText}>
            <h2 className={styles.title}>
              Walk-ins
              {entries.length > 0 && (
                <span className={styles.countBadge}>{entries.length}</span>
              )}
              <WalkInLink />
            </h2>
          </Box>
          <ActionIcon
            variant="subtle"
            color="gray"
            size={34}
            radius="xl"
            className={styles.close}
            onClick={drawer.close}
            aria-label="Close"
          >
            <DESIGN_ICONS.X size={18} />
          </ActionIcon>
        </Box>
        <Box className={styles.body}>
          {store.loaded || entries.length > 0 ? (
            <WaitlistList
              entries={entries}
              timezone={timezone}
              services={services}
              now={nowMs}
              onReorder={(ids) => void actions.reorder(ids)}
              onRemove={(entry) => void actions.remove(entry)}
              onEdit={drawer.startEdit}
              renderActions={(entry, slot) => (
                <AssignPicker
                  client={entryClientName(entry)}
                  rows={qualifiedFor(
                    rows,
                    serviceOf(entry.salon_service_uuid),
                    nowWall,
                  )}
                  requestedId={entry.preferred_staff_id}
                  onOpenChange={slot.onOpenChange}
                  onAssign={(choice, mode) =>
                    actions.assign(entry, choice, mode, availability)
                  }
                />
              )}
            />
          ) : (
            <Text size="sm" c="dimmed">
              Loading…
            </Text>
          )}
        </Box>
        <Box className={styles.footer}>
          <Button
            fullWidth
            size="md"
            h={44}
            leftSection={<DESIGN_ICONS.Plus size={17} />}
            onClick={drawer.startNew}
          >
            New Walk-In
          </Button>
        </Box>
      </Box>

      {form && (
        <WalkInForm
          // Fresh fields per target (new vs each edited row).
          key={form.mode === "edit" ? form.entry.uuid : "new"}
          target={form}
          services={services}
          availability={{ rows, nowMs, nowWall, timezone }}
          onCancel={drawer.closeForm}
          onSave={async (values) => {
            const saved = await actions.save(form, values, availability);
            if (saved) drawer.closeForm();
            return saved;
          }}
          frame={({ title, subtitle, body, footer }) => (
            <NestedScreen
              title={title}
              subtitle={subtitle}
              onBack={drawer.closeForm}
              animate={!drawer.instant}
              footer={footer}
            >
              {body}
            </NestedScreen>
          )}
        />
      )}
    </>
  );
}
